import { useId, useRef, useState } from 'react';
import { api, type Mes } from '../services/api';
import { Aviso, Boton } from './ui';

/** Solo se monta para el admin de un mes abierto con partidos. */
export function CierreMes({ datos, onCerrado }: { datos: Mes; onCerrado: (mes: Mes) => void }) {
  const dialogo = useRef<HTMLDialogElement>(null);
  const cancelar = useRef<HTMLButtonElement>(null);
  const titulo = useId();
  const [cerrando, setCerrando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const enCurso = useRef(false);
  const pendientes = datos.pendientes ?? 0;

  async function cerrar() {
    if (enCurso.current) return;
    enCurso.current = true;
    setCerrando(true);
    setError(null);
    try {
      const mes = await api.cerrarMes(datos.mes);
      dialogo.current?.close();
      onCerrado(mes);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cerrar el mes. Volvé a intentar.');
    } finally {
      enCurso.current = false;
      setCerrando(false);
    }
  }

  return <>
    <div className="flex flex-wrap items-center justify-between gap-x-3 rounded-xl border border-(--color-borde) px-3 py-2 text-sm">
      <p className="text-(--color-apagado)">¿Ya terminaron de jugar este mes?</p>
      <button type="button" aria-haspopup="dialog"
        onClick={() => { setError(null); dialogo.current?.showModal(); cancelar.current?.focus(); }}
        className="min-h-11 font-semibold text-(--color-acento) underline-offset-4 hover:underline">
        Cerrar mes ahora
      </button>
    </div>
    <dialog ref={dialogo} aria-labelledby={titulo} aria-busy={cerrando}
      onCancel={e => { if (enCurso.current) e.preventDefault(); }}
      onClick={e => { if (e.target === e.currentTarget && !enCurso.current) dialogo.current?.close(); }}
      className="m-auto w-[calc(100%-2rem)] max-w-sm rounded-2xl border border-(--color-acento) bg-(--color-panel) p-5 text-(--color-texto) backdrop:bg-black/60">
      <h2 id={titulo} className="mb-3 font-titulo text-2xl">Cerrar {datos.titulo}</h2>
      <div className="flex flex-col gap-3 text-sm leading-relaxed">
        <p>Confirmás que el grupo ya terminó de jugar este mes.</p>
        <p>{pendientes === 0
          ? 'No hay partidos pendientes. Se definirán el campeón y los reconocimientos.'
          : `${pendientes === 1 ? 'Se confirmará el partido pendiente' : `Se confirmarán los ${pendientes} partidos pendientes`} y se definirán el campeón y los reconocimientos.`} El resumen y la placa quedarán disponibles para todo el grupo.</p>
        <p className="text-(--color-apagado)">Si después falta cargar un partido, deberá hacerlo un administrador. Si cambia el resultado del mes, habrá que generar la imagen nuevamente.</p>
        {error && <Aviso tono="error">{error}</Aviso>}
      </div>
      <div className="mt-5 flex flex-col gap-2">
        <Boton onClick={() => { void cerrar(); }} deshabilitado={cerrando} ancho>
          {cerrando ? 'Cerrando mes…' : 'Confirmar cierre del mes'}
        </Boton>
        <button ref={cancelar} type="button" disabled={cerrando} onClick={() => dialogo.current?.close()}
          className="min-h-11 rounded-xl border border-(--color-borde) disabled:opacity-40">Cancelar</button>
      </div>
    </dialog>
  </>;
}
